import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { money, num, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  TrendingUp, ReceiptText, Wallet, HandCoins, AlertTriangle, Package, Users, ArrowUpRight
} from "lucide-react";

const PIE = ["#1A2A5E", "#F4571E", "#10B981", "#F59E0B", "#8B5CF6"];

function KpiCard({ label, value, icon: Icon, tint, testid, onClick }) {
  return (
    <Card onClick={onClick} className={`kpi-card cursor-pointer p-5 border-slate-200 bg-white ${onClick ? "" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon className="h-5 w-5" />
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-300" />
      </div>
      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-2xl font-bold font-mono text-[#1A2A5E] mt-1">{value}</div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
    );
  }

  const d = data || {};

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#0F172A]">Dashboard</h2>
        <p className="text-slate-500 text-sm">Live overview of your store performance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <KpiCard testid="kpi-today-sales" label="Today's Sales" value={money(d.today_sales)} icon={TrendingUp} tint="bg-emerald-50 text-emerald-600" onClick={() => navigate("/bills")} />
        <KpiCard testid="kpi-today-bills" label="Today's Bills" value={num(d.today_bills)} icon={ReceiptText} tint="bg-blue-50 text-blue-600" onClick={() => navigate("/bills")} />
        <KpiCard testid="kpi-today-profit" label="Today's Profit" value={money(d.today_profit)} icon={Wallet} tint="bg-indigo-50 text-indigo-600" />
        <KpiCard testid="kpi-outstanding" label="Outstanding Credit" value={money(d.outstanding_credit)} icon={HandCoins} tint="bg-amber-50 text-amber-600" onClick={() => navigate("/customers")} />
        <KpiCard testid="kpi-low-stock" label="Low Stock" value={num(d.low_stock_count)} icon={AlertTriangle} tint="bg-rose-50 text-rose-600" onClick={() => navigate("/products")} />
        <KpiCard testid="kpi-total-products" label="Total Products" value={num(d.total_products)} icon={Package} tint="bg-cyan-50 text-cyan-600" onClick={() => navigate("/products")} />
        <KpiCard testid="kpi-total-customers" label="Total Customers" value={num(d.total_customers)} icon={Users} tint="bg-violet-50 text-violet-600" onClick={() => navigate("/customers")} />
        <KpiCard testid="kpi-month-payments" label="This Month (Paid)" value={money(Object.values(d.payment_summary || {}).reduce((a, b) => a + b, 0))} icon={TrendingUp} tint="bg-[#F4571E]/10 text-[#F4571E]" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200">
          <h3 className="font-display font-semibold text-[#0F172A] mb-4">Sales — Last 7 Days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.sales_trend || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F7" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#F8FAFC" }} />
              <Bar dataKey="total" fill="#1A2A5E" radius={[6, 6, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-display font-semibold text-[#0F172A] mb-4">Payment Summary</h3>
          {Object.keys(d.payment_summary || {}).length ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={Object.entries(d.payment_summary).map(([name, value]) => ({ name, value }))} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {Object.keys(d.payment_summary).map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-slate-400 py-10 text-center">No payments yet</div>}
          <div className="space-y-1.5 mt-3">
            {Object.entries(d.payment_summary || {}).map(([k, v], i) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 capitalize text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE[i % PIE.length] }} />{k}</span>
                <span className="font-mono font-medium">{money(v)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="lg:col-span-2 p-5 border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-[#0F172A]">Recent Bills</h3>
            <button onClick={() => navigate("/bills")} className="text-xs font-semibold text-[#F4571E]">View all</button>
          </div>
          <div className="divide-y divide-slate-100">
            {(d.recent_bills || []).map((b) => (
              <div key={b.id} className="flex items-center justify-between py-2.5" data-testid={`recent-bill-${b.bill_no}`}>
                <div>
                  <div className="font-medium text-sm text-slate-800">{b.bill_no}</div>
                  <div className="text-xs text-slate-400">{b.customer_name} · {fmtDateTime(b.created_at)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-semibold text-sm text-[#1A2A5E]">{money(b.grand_total)}</div>
                  <Badge variant="secondary" className="text-[10px] capitalize">{b.payment_method}</Badge>
                </div>
              </div>
            ))}
            {!(d.recent_bills || []).length && <div className="text-sm text-slate-400 py-8 text-center">No bills yet</div>}
          </div>
        </Card>

        <div className="space-y-4 md:space-y-6">
          <Card className="p-5 border-slate-200">
            <h3 className="font-display font-semibold text-[#0F172A] mb-3">Top Selling</h3>
            <div className="space-y-2.5">
              {(d.top_products || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-700 truncate">
                    <span className="h-6 w-6 rounded-md bg-[#1A2A5E] text-white text-[11px] flex items-center justify-center font-semibold shrink-0">{i + 1}</span>
                    <span className="truncate">{p.name}</span>
                  </span>
                  <span className="font-mono text-slate-500 shrink-0">{num(p.qty)}</span>
                </div>
              ))}
              {!(d.top_products || []).length && <div className="text-sm text-slate-400 py-4 text-center">No data</div>}
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h3 className="font-display font-semibold text-[#0F172A] mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-rose-500" /> Low Stock Alerts</h3>
            <div className="space-y-2">
              {(d.low_stock_items || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <span className="truncate text-slate-700">{p.name}</span>
                  <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50 shrink-0">{num(p.current_stock)} {p.unit}</Badge>
                </div>
              ))}
              {!(d.low_stock_items || []).length && <div className="text-sm text-slate-400 py-4 text-center">All stocked up 🎉</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
